import {
    MouseEventHandler,
    TouchEventHandler,
    useEffect,
    useRef,
    useState,
} from 'react'
import Image from 'next/image'
import { styled } from '@/src/stitches.config'
import arrowLoop from '@/public/images/arrow_loop.svg'
import HandwrittenText from '@/src/common/components/marketing/text/HandwrittenText'
import { ClipLoader } from 'react-spinners'
import { trackUnwrapEvent } from '@/src/common/utilities/gtag'
import { TGiftForWrappedCard } from '@/src/common/components/marketing/cards/types'

const ComponentWrapper = styled('div', {
    padding: '10px 10px 10px 0',
    width: 'fit-content',
    maxWidth: '100%',
    '.root': {
        width: 400,
        height: 'auto',
        maxWidth: '100%',
        position: 'relative',
        zIndex: 1,
        aspectRatio: '1.493/1',
    },
    '.draggableImage': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 0,
        cursor: 'grab',
    },
    '.wrapperImageFront': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: -2,
        visibility: 'hidden',
        br: 16,
        cursor: 'grab',
    },
    '.wrapperBackPanel': {
        background: 'linear-gradient(to right, gray, white)',
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '-100%',
        zIndex: -1,
        transformOrigin: 'center right',
        visibility: 'hidden',
        willChange: 'transform',
        boxShadow: 'gray 0px 1px 5px -1px',
        br: 16,
        cursor: 'grab',
    },
    '.brandImage': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: -3,
        br: 16,
        boxShadow: 'gray 0px 1px 5px -1px',
        objectFit: 'cover',
        cursor: 'grab',
    },
    variants: {
        size: {
            'xs-mobile': {
                '@media (max-width: 425px)': {
                    width: '95%',
                    maxWidth: 330,
                    margin: 'auto',
                    pb: 0,
                    pr: 0,
                },
            },
        },
    },
})

const SwipeToOpen = styled('div', {
    display: 'flex',
    fd: 'row',
    ai: 'flex-end',
    height: 'fit-content',
    width: 'fit-content',
    minHeight: '50px',
    pt: 10,
    ml: 25,
    img: {
        transform: 'scaleX(-1) rotate(-120deg)',
        width: 35,
        height: 'auto',
        mb: 5,
        mr: 5,
    },
})

const DragExtensionZone = styled('div', {
    position: 'absolute',
    top: '-60%',
    left: '-50%',
    zIndex: -5,
    pointerEvents: 'none',
})

const LoadingWrapper = styled('div', {
    display: 'flex',
    ai: 'center',
    jc: 'center',
    height: '100%',
    m: 0,
})

// Provide 1 starter point and the y coordinate to get the x coordinate
// using point-slope form
// y − y1 = m(x − x1)
const getXIntercept = (x1: number, y1: number, y: number, slope: number) => {
    return (y - y1) / slope + x1
}

type TWrappedCardProps = {
    brandImage: string
    wrapperImage?: string | null
    gift?: TGiftForWrappedCard | null
    children?: JSX.Element | JSX.Element[] | null
    sendUnwrapEvent?: boolean
    willAutoShut?: boolean
    imagePriority?: boolean
    swipeToOpen?: boolean
    swipeStyle?: object
    onMouseDown?: MouseEventHandler<HTMLDivElement>
    onTouchMove?: TouchEventHandler<HTMLDivElement>
    dataTestId?: string
    size?: 'xs-mobile'
}

const WrappedCard = ({
    brandImage,
    gift,
    children,
    onTouchMove,
    onMouseDown,
    wrapperImage = '',
    sendUnwrapEvent = false,
    willAutoShut = false,
    imagePriority = false,
    swipeToOpen = false,
    swipeStyle = {},
    dataTestId = '',
    size = undefined,
}: TWrappedCardProps) => {
    const wrapperImageFrontRef = useRef<null | HTMLImageElement>(null)
    const wrapperBackPanelRef = useRef<null | HTMLDivElement>(null)
    const rootRef = useRef<null | HTMLDivElement>(null)
    const [autoOpening, setAutoOpening] = useState<boolean>()
    const [autoOpened, setAutoOpened] = useState<boolean>(false)
    const [isUnwrapEventSent, setIsUnwrapEventSent] = useState<boolean>(false)
    const [rootWidth, setRootWidth] = useState<number>(0)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const rootHeight = (262 / 300) * rootWidth
    const requestRef = useRef<any>(null)
    const previousTimeRef = useRef(null)

    const handleDragEnd = () => {
        document.body.classList.remove('no-scroll')
    }

    const handleDragStart = () => {
        document.body.classList.add('no-scroll')
    }

    const handleDragOver = (e: any) => {
        // @ts-ignore
        const rect = document
            .getElementById('extension-zone')
            .getBoundingClientRect()
        handleDrag({
            x: e.clientX - rect.left - 180,
            y: e.clientY - rect.top - 170,
        })
    }

    const handleDrag = (point: any) => {
        if (autoOpening) {
            return
        }
        const cursorPoint = {
            x: point.x,
            y: Math.max(
                rootHeight * -0.5 + 2,
                Math.min(rootHeight * 1.5 - 2, point.y)
            ),
        }
        if (cursorPoint.x < rootWidth) {
            setAutoOpened(false)
        }
        if (
            (cursorPoint.x >= rootWidth - 20 ||
                cursorPoint.y <= rootHeight - 425) &&
            !autoOpened
        ) {
            requestAnimationFrame((startingTime) =>
                autoOpen(cursorPoint, startingTime)
            )
            return
        }
        if (autoOpened) {
            return
        }
        dragToPoint(cursorPoint)
    }

    const handleWrapperImageTouch = (e: any) => {
        // @ts-ignore
        const rect = document
            .getElementById('extension-zone')
            .getBoundingClientRect()
        handleDrag({
            x: e.targetTouches[0].clientX - rect.left - 170,
            y: e.targetTouches[0].clientY - rect.top - 100,
        })
    }

    const dragToPoint = (cursorPoint: any) => {
        if (!wrapperImage) {
            return
        }

        const gridPoint = {
            x: Math.max(1, cursorPoint.x),
            y: rootHeight / 2 - cursorPoint.y,
        }

        const perpendicularSlope = (gridPoint.x / gridPoint.y) * -1
        const midPoint = {
            x: cursorPoint.x / 2,
            y: -1 * ((cursorPoint.y - rootHeight / 2.5) / 2 + rootHeight / 3),
        }

        const clipLineTopX = getXIntercept(
            midPoint.x,
            midPoint.y,
            20,
            perpendicularSlope
        )
        const clipLineBottomX = getXIntercept(
            midPoint.x,
            midPoint.y,
            -1 * (rootHeight + 20),
            perpendicularSlope
        )

        const degrees =
            (Math.atan(gridPoint.y / gridPoint.x) * -2 * 180) / Math.PI

        const imageFrontClipPath = `path('M ${clipLineTopX} -20 L ${
            rootWidth + 20
        },-20 L ${rootWidth + 20},${rootHeight + 20} L ${clipLineBottomX},${
            rootHeight + 20
        }')`
        let backPanelClipPath = `path('M ${rootWidth - clipLineTopX} -20 L ${
            rootWidth + 20
        },-20 L ${rootWidth + 20},${rootHeight + 20} L ${
            rootWidth - clipLineBottomX
        },${rootHeight + 20}')`

        const translateLeft = gridPoint.x
        const translateTop = cursorPoint.y
        const opacity = (rootWidth * 2 - translateLeft) / 100

        // handles box shadow issue when completely unwrapped
        if (translateLeft === 1 && willAutoShut) {
            backPanelClipPath = `path('M ${
                rootWidth - clipLineTopX + 5
            } -20 L ${rootWidth + 20},-20 L ${rootWidth + 20},${
                rootHeight + 20
            } L ${rootWidth - clipLineBottomX + 5},${rootHeight + 20}')`
        }

        if (wrapperImageFrontRef.current) {
            wrapperImageFrontRef.current.style.clipPath = imageFrontClipPath
            wrapperImageFrontRef.current.style.visibility = 'visible'
            wrapperImageFrontRef.current.style.opacity =
                translateLeft === 2 && translateTop === rootHeight / 2
                    ? '0'
                    : `${opacity}`
        }

        if (wrapperBackPanelRef.current) {
            wrapperBackPanelRef.current.style.clipPath = backPanelClipPath
            wrapperBackPanelRef.current.style.opacity =
                translateLeft === 2 && translateTop === rootHeight / 2
                    ? '0'
                    : `${opacity}`
            wrapperBackPanelRef.current.style.transform = `translate(${translateLeft}px, ${translateTop}px) rotate(${degrees}deg)`
            wrapperBackPanelRef.current.style.visibility = 'visible'
        }
    }

    const autoOpen = (
        startingCursorPoint: any,
        startingTime: any,
        now?: any
    ) => {
        setAutoOpening(true)
        let peelingCursorPoint
        const duration = 600
        const xRange = Math.abs(startingCursorPoint.x - rootWidth * 2)

        if (previousTimeRef.current !== null && now) {
            const deltaTime = now - startingTime

            peelingCursorPoint = {
                x: startingCursorPoint.x + (deltaTime / duration) * xRange,
                y: startingCursorPoint.y,
            }
            dragToPoint(peelingCursorPoint)

            if (peelingCursorPoint.x > rootWidth * 2) {
                cancelAnimationFrame(requestRef.current)
                previousTimeRef.current = null
                setAutoOpening(false)
                setAutoOpened(true)
                if (!isUnwrapEventSent && sendUnwrapEvent && gift) {
                    trackUnwrapEvent(gift)
                    setIsUnwrapEventSent(true)
                }
                return
            }
        }
        previousTimeRef.current = now
        requestRef.current = requestAnimationFrame((now) => {
            autoOpen(startingCursorPoint, startingTime, now)
        })
    }

    const autoShut = (
        startingCursorPoint: any,
        startingTime: any,
        now?: any
    ) => {
        let peelingCursorPoint = startingCursorPoint
        const duration = 600 // we want the animation to take this long
        const endingCursorPoint = { x: 1, y: rootHeight / 2 }
        const xRange = Math.abs(startingCursorPoint.x - endingCursorPoint.x)
        const yRange = Math.abs(startingCursorPoint.y - endingCursorPoint.y)

        if (previousTimeRef.current !== null && now) {
            const deltaTime = now - startingTime

            peelingCursorPoint = {
                x: Math.max(
                    1,
                    startingCursorPoint.x - (deltaTime / duration) * xRange
                ),
                y: Math.min(
                    rootHeight / 2,
                    startingCursorPoint.y +
                        (deltaTime / duration) * yRange * 1.4
                ),
            }
            dragToPoint(peelingCursorPoint)

            if (peelingCursorPoint.x <= 1) {
                cancelAnimationFrame(requestRef.current)
                previousTimeRef.current = null
                return
            }
        }

        previousTimeRef.current = now
        requestRef.current = requestAnimationFrame((now) => {
            autoShut(startingCursorPoint, startingTime, now)
        })
    }

    // shows a loader when loading new wrapper image
    useEffect(() => {
        setIsLoading(!!wrapperImage)
    }, [wrapperImage])

    useEffect(() => {
        if (rootRef.current?.offsetWidth) {
            setRootWidth(rootRef.current?.offsetWidth)
        }
    }, [rootRef.current?.offsetWidth])

    useEffect(() => {
        if (wrapperImage && rootWidth > 0) {
            if (willAutoShut) {
                setTimeout(() => {
                    const startingCursorPoint = {
                        x: rootWidth,
                        y: rootHeight / 2.009,
                    }
                    dragToPoint(startingCursorPoint)
                    requestAnimationFrame((startingTime) =>
                        autoShut(startingCursorPoint, startingTime)
                    )
                }, 600)
            } else {
                const startingCursorPoint = { x: 1, y: rootHeight / 2.009 }
                dragToPoint(startingCursorPoint)
            }
        }
    }, [wrapperImage, rootWidth])

    useEffect(() => {
        const resize = () => {
            if (rootRef.current?.offsetWidth) {
                setRootWidth(rootRef.current?.offsetWidth)
            }
        }
        window.addEventListener('resize', resize)
        return () => {
            window.removeEventListener('resize', resize)
        }
    }, [])

    return (
        <ComponentWrapper
            onDragOver={handleDragOver}
            onTouchMove={onTouchMove ?? undefined}
            onMouseDown={onMouseDown ?? undefined}
            data-testid={dataTestId}
            size={size}
        >
            <div className={'root'} ref={rootRef}>
                <DragExtensionZone
                    id={'extension-zone'}
                    style={{
                        width: rootWidth * 2,
                        height: rootHeight * 1.75,
                    }}
                />
                <Image
                    className={'draggableImage'}
                    alt="Personalized Gift Wrapper Image"
                    src={
                        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
                    }
                    draggable={true}
                    width={400}
                    height={268}
                    onTouchMove={handleWrapperImageTouch}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onTouchStart={handleDragStart}
                    onTouchEnd={handleDragEnd}
                    priority={imagePriority}
                />

                {isLoading && (
                    <LoadingWrapper>
                        <ClipLoader size={40} color={'lightgray'} />
                    </LoadingWrapper>
                )}

                {wrapperImage && !!wrapperImageFrontRef && !autoOpened && (
                    <>
                        <Image
                            className={'wrapperImageFront'}
                            alt={'Personalized Gift Wrapper Image'}
                            draggable={false}
                            src={wrapperImage}
                            ref={wrapperImageFrontRef}
                            width={400}
                            height={268}
                            priority={imagePriority}
                            onLoadingComplete={() => {
                                setIsLoading(false)
                            }}
                        />
                        <div
                            className={'wrapperBackPanel'}
                            ref={wrapperBackPanelRef}
                            draggable={false}
                            style={{
                                top: `${(-1 * rootHeight) / 2}px`,
                            }}
                        />
                    </>
                )}

                {brandImage && (
                    <Image
                        className={'brandImage'}
                        alt="Gift Brand Image"
                        src={brandImage}
                        width={400}
                        height={268}
                        draggable={false}
                        priority={imagePriority}
                        style={{ display: 'block' }}
                    />
                )}
                {children && <>{children}</>}
            </div>
            {swipeToOpen && wrapperImage && !autoOpened && (
                <SwipeToOpen
                    id={'swipe-to-open'}
                    data-testid={'swipe'}
                    style={swipeStyle}
                >
                    <Image
                        alt="Swipe to Open"
                        className={'swipe-to-open-graphic'}
                        src={arrowLoop}
                        height={50}
                        width={40}
                    />
                    <HandwrittenText
                        text={'Swipe to open'}
                        transform={''}
                        size={5}
                        marginBottom={0}
                    />
                </SwipeToOpen>
            )}
        </ComponentWrapper>
    )
}

export default WrappedCard

WrappedCard.defaultProps = {
    wrapperImage: '',
    brandImage: '',
    gift: null,
    children: null,
    sendUnwrapEvent: false,
    willAutoShut: false,
    imagePriority: false,
    swipeToOpen: false,
    swipeStyle: {},
}
